import type { UpdateChatInput } from '../../../../generated/graphql-manager';

import {
  Heading,
  Paragraph,
  Radio,
  SelectionInputLayout,
  Toggle,
  classNames,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { InfoTooltip } from 'src/components/info-tooltip';

import {
  ChatType,
  State,
  useCreateChatMutation,
  useDeleteChatMutation,
  useGetChatTypeQuery,
  useUpdateChatMutation,
} from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { SetupPageFooter } from '../../../components/setup-page-footer';
import { useUserPermissions } from '../../../hooks/user-permissions';

import styles from './messaging.module.css';
import managerStyles from '../../../manager.module.css';

export function MessagingPage() {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const routes = useAbsoluteRoutes();
  const { isEventEditingAllowed } = useUserPermissions();

  const getChatTypeQueryResponse = useGetChatTypeQuery({ variables: { webcastId } });
  const webcastState = getChatTypeQueryResponse.data?.webcast?.state;
  const FORM_ENABLED_STATES = [State.PRELIVE, State.LIVE, State.LIVE_TEST];
  const formDisabled = !webcastState || !FORM_ENABLED_STATES.includes(webcastState) || !isEventEditingAllowed;
  const loading = getChatTypeQueryResponse.loading;

  const chatSettings = getChatTypeQueryResponse.data?.webcast?.chat?.settings;
  const chatType = chatSettings?.type;
  const useProvidedUsername =
    chatSettings && 'useProvidedUsername' in chatSettings ? (chatSettings.useProvidedUsername ?? false) : false;
  const allowBeforeLive = chatSettings?.allowBeforeLive ?? false;

  const [createChatMutation] = useCreateChatMutation();

  const createChat = (type: ChatType) => {
    createChatMutation({
      variables: { input: { webcastId, type } },
      optimisticResponse: { createChat: { __typename: 'CreateChatSuccess' } },
      update: (cache, { data }) => {
        if (data?.createChat.__typename !== 'CreateChatSuccess') return;

        const __typename = {
          [ChatType.UNMODERATED]: 'UnmoderatedChatSettings',
          [ChatType.MODERATED]: 'ModeratedChatSettings',
          [ChatType.ANONYMOUS]: 'AnonymousChatSettings',
        }[type];

        const settings =
          type === ChatType.ANONYMOUS
            ? { __typename, type }
            : { __typename, type, useProvidedUsername: true, allowBeforeLive: false };

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: {
            chat: () => ({
              __typename: 'Chat',
              settings,
            }),
          },
        });
      },
      onCompleted: (data) => {
        if (data?.createChat.__typename === 'CreateChatSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const [updateChatMutation] = useUpdateChatMutation();

  const updateChat = (input: UpdateChatInput) => {
    updateChatMutation({
      variables: { input },
      optimisticResponse: { updateChat: { __typename: 'UpdateChatSuccess' } },
      update: (cache, { data }) => {
        if (data?.updateChat.__typename !== 'UpdateChatSuccess') return;

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: {
            chat: (chat) => {
              const { webcastId: _, ...settings } = input;
              return { ...chat, settings: { ...chat.settings, ...settings } };
            },
          },
        });
      },
      onCompleted: (data) => {
        if (data?.updateChat.__typename === 'UpdateChatSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const [deleteChatMutation] = useDeleteChatMutation();

  const deleteChat = () => {
    deleteChatMutation({
      variables: { webcastId },
      optimisticResponse: { deleteChat: { __typename: 'DeleteChatSuccess' } },
      update: (cache, { data }) => {
        if (data?.deleteChat.__typename !== 'DeleteChatSuccess') return;

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: { chat: () => null },
        });
      },
      onCompleted: (data) => {
        if (data?.deleteChat.__typename === 'DeleteChatSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  const handleChangeChatType = (newType?: ChatType) => () => {
    if (newType)
      if (chatType) updateChat({ webcastId, type: newType });
      else createChat(newType);
    else deleteChat();
  };

  const chatFeatureToggles = (
    <fieldset className={styles.fieldset} disabled={formDisabled}>
      <SelectionInputLayout
        hintMessage={t('manager.webcastSetup.messaging.features.useProvidedUsername.description')}
        checked={useProvidedUsername}
        disabled={formDisabled}
        variant="ghost"
        inputElement={
          <Toggle
            data-testid="use-provided-username-toggle"
            checked={useProvidedUsername}
            disabled={formDisabled}
            onChange={() => updateChat({ webcastId, useProvidedUsername: !useProvidedUsername })}
          >
            <span className={styles.toggleLabel}>
              {t('manager.webcastSetup.messaging.features.useProvidedUsername.label')}
              <InfoTooltip label={t('manager.webcastSetup.messaging.features.useProvidedUsername.tooltip')} />
            </span>
          </Toggle>
        }
      />

      <SelectionInputLayout
        hintMessage={t('manager.webcastSetup.messaging.features.preLiveChat.description')}
        checked={allowBeforeLive}
        disabled={formDisabled}
        variant="ghost"
        inputElement={
          <Toggle
            data-testid="pre-live-chat-toggle"
            checked={allowBeforeLive}
            disabled={formDisabled}
            onChange={() => updateChat({ webcastId, allowBeforeLive: !allowBeforeLive })}
          >
            {t('manager.webcastSetup.messaging.features.preLiveChat.label')}
          </Toggle>
        }
      />
    </fieldset>
  );

  return (
    <main className={classNames(managerStyles.main, styles.wrapper)} data-testid="messaging-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.messaging.title')}</Heading>
      <Paragraph className={managerStyles.grayText}>{t('manager.webcastSetup.messaging.description')}</Paragraph>

      <div className={styles.options}>
        <SelectionInputLayout
          checked={chatType === undefined}
          disabled={formDisabled || loading}
          inputElement={
            <Radio
              data-testid="disabled-chat-option"
              name="chat-type"
              value={undefined}
              checked={chatType === undefined}
              disabled={formDisabled || loading}
              onChange={handleChangeChatType(undefined)}
            >
              {t('manager.webcastSetup.messaging.options.disabledChat.label')}
            </Radio>
          }
        />

        <SelectionInputLayout
          hintMessage={t('manager.webcastSetup.messaging.options.unmoderatedChat.description')}
          checked={chatType === ChatType.UNMODERATED}
          disabled={formDisabled || loading}
          inputElement={
            <Radio
              data-testid="unmoderated-chat-option"
              name="chat-type"
              value={ChatType.UNMODERATED}
              checked={chatType === ChatType.UNMODERATED}
              disabled={formDisabled || loading}
              onChange={handleChangeChatType(ChatType.UNMODERATED)}
            >
              {t('manager.webcastSetup.messaging.options.unmoderatedChat.label')}
            </Radio>
          }
        />
        {chatType === ChatType.UNMODERATED && chatFeatureToggles}

        <SelectionInputLayout
          hintMessage={t('manager.webcastSetup.messaging.options.moderatedChat.description')}
          checked={chatType === ChatType.MODERATED}
          disabled={formDisabled || loading}
          inputElement={
            <Radio
              data-testid="moderated-chat-option"
              name="chat-type"
              value={ChatType.MODERATED}
              checked={chatType === ChatType.MODERATED}
              disabled={formDisabled || loading}
              onChange={handleChangeChatType(ChatType.MODERATED)}
            >
              {t('manager.webcastSetup.messaging.options.moderatedChat.label')}
            </Radio>
          }
        />
        {chatType === ChatType.MODERATED && chatFeatureToggles}
      </div>

      <SetupPageFooter
        nextLabel={t('manager.webcastSetup.messaging.nextStep.label')}
        route={routes.webcastSetup_eventRecording}
      />
    </main>
  );
}
